import {
  FC,
  ReactNode,
  useState,
  useCallback,
  SyntheticEvent,
  createContext,
  useContext,
} from 'react';
import { useLocation } from 'react-router';

import Tabs, { TabsProps } from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TabPanel from 'components/TabPanel';

import { BASE } from 'const';

type TabDefinition = {
  title: ReactNode;
  component: ReactNode;
  hash: string;
};

type Props = {
  tabs: TabDefinition[];
  navigate?: boolean;
  dismount?: boolean;
} & TabsProps;

type TabberContextType = {
  changeTab: (hash: string) => void;
};

const TabberContext = createContext<TabberContextType>({
  changeTab: () => {},
});

const index = (tabs: TabDefinition[], hash: string) => {
  let index = 0;

  for (let tab of tabs) {
    if (tab.hash === hash) return index;

    index++;
  }

  return 0;
};

export const useTabber = () => useContext(TabberContext);

const Tabber: FC<Props> = ({ tabs, navigate = true, dismount, ...rest }) => {
  const location = useLocation();

  const [current, setCurrent] = useState(
    location.hash.replace('#', '') ?? tabs[0].hash
  );

  const changeTab = useCallback(
    (hash: string) => {
      setCurrent(hash);

      if (!navigate) return;

      window.history.replaceState(
        {},
        document.title,
        `${BASE}${location.pathname}#${hash}`
      );
    },
    [location.pathname, navigate]
  );

  const handleTabClick = useCallback(
    (_: SyntheticEvent, i: number) => {
      changeTab(tabs[i].hash);
    },
    [changeTab, tabs]
  );

  const currentIndex = index(tabs, current);

  return (
    <TabberContext.Provider value={{ changeTab }}>
      <Box sx={{ width: '100%' }}>
        <Paper>
          <Tabs value={currentIndex} onChange={handleTabClick} {...rest}>
            {tabs.map(({ title }, i) => (
              <Tab label={title} key={i} />
            ))}
          </Tabs>
        </Paper>

        {tabs.map(({ component }, i) => (
          <TabPanel value={currentIndex} dismount={dismount} key={i} index={i}>
            {component}
          </TabPanel>
        ))}
      </Box>
    </TabberContext.Provider>
  );
};

export default Tabber;
